/**
 * Lightbox for full-size image attachments. Triggered from any
 * thumbnail in a message bubble OR from the composer staging
 * preview.
 *
 * Single-image lightbox for now (no carousel). Mounted at the
 * dashboard root so it overlays the entire UI when open.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface ImageLightboxContextValue {
  src: string | null;
  open: (src: string) => void;
  close: () => void;
}

const ImageLightboxContext = createContext<ImageLightboxContextValue | null>(
  null,
);

export function ImageLightboxProvider({ children }: { children: ReactNode }) {
  const [src, setSrc] = useState<string | null>(null);
  const open = useCallback((next: string) => setSrc(next), []);
  const close = useCallback(() => setSrc(null), []);
  const value = useMemo(() => ({ src, open, close }), [src, open, close]);
  return (
    <ImageLightboxContext.Provider value={value}>
      {children}
    </ImageLightboxContext.Provider>
  );
}

export function useImageLightbox(): ImageLightboxContextValue {
  const ctx = useContext(ImageLightboxContext);
  if (!ctx) {
    throw new Error(
      'useImageLightbox must be used inside <ImageLightboxProvider>',
    );
  }
  return ctx;
}
